import {
  DAY_MONTH_SHORT_FORMAT,
  HOURS_CAP_AM_PM_FORMAT,
  MONTH_DAY_YEAR_SLASH_FORMAT,
  PRETTY_DAY_MONTH_NUM_DAY_FORMAT,
  PRETTY_MONTH_DAY_YEAR_FORMAT,
  TIME_CAP_AM_PM_FORMAT,
  DAY_MONTH_YEAR_SLASH_FORMAT,
  PRETTY_MONTH_NUM_DAY_FORMAT,
} from '~/utils/gcxDates/config/dateFormats'
import format from '~/utils/gcxDates/format'
import add from '~/utils/gcxDates/add'
import getLongDateFormat from '~/utils/gcxDates/getLongDateFormat'
import getShortTimeZone from '~/utils/gcxDates/getShortTimeZone'
import isSame from '~/utils/gcxDates/isSame'
import startOfDay from '~/utils/gcxDates/startOf/day'
import isBeforeDate from '~/utils/gcxDates/isBeforeDate'
import distanceFromNowInWords from '~/utils/gcxDates/distanceFromNow'
import getDaysDiff from '~/utils/gcxDates/getDaysDiff'
import getMillisecondsDiff from '~/utils/gcxDates/getMillisecondsDiff'
import dateIsValid from '~/utils/gcxDates/dateIsValid'
import getLocale from '~/utils/gcxDates/getLocale'
import { getCalendarDaysDiff } from '~/utils/gcxDates/getCalendarDaysDiff'

import { Scalars } from '~/common/types/graphql'

export const lastActivityStatus = (dateTime: Scalars['DateTimeType']) => {
  if (!dateTime)
    return {
      isOnline: false,
      lastOnline: '',
    }
  const date = new Date(dateTime)

  const isOnline = Math.abs(getMillisecondsDiff(dateTime, new Date())) < 600000

  const lastOnline = isOnline
    ? 'Online'
    : dateIsValid(date)
      ? distanceFromNowInWords(date)
      : ''

  return {
    isOnline,
    lastOnline,
  }
}

export const prettyDateTime = (date: Scalars['DateType']) => {
  if (!date) return 'N/A'
  const localeDateTimeFormat = getLongDateFormat() + ' ' + TIME_CAP_AM_PM_FORMAT
  return format(date, localeDateTimeFormat)
}

export const defaultNavigatorTimeFormat = (): string => {
  const navigatorLanguageIsUS =
    navigator.language === 'en-US' || navigator.language === 'en'

  return navigatorLanguageIsUS
    ? MONTH_DAY_YEAR_SLASH_FORMAT
    : DAY_MONTH_YEAR_SLASH_FORMAT
}

export const prettyDate = (date: Date | null) => {
  if (!date) return 'N/A'

  const timeFormat = defaultNavigatorTimeFormat()

  return format(date, timeFormat)
}

export const prettyTimeEntryModalDateTime = (date: Scalars['DateType']) => {
  if (!date) return 'N/A'
  const locale = getLocale()
  return format(date, PRETTY_DAY_MONTH_NUM_DAY_FORMAT, {
    locale,
  })
}

export const daysUntil = (date: Scalars['DateType']) => {
  const end = new Date(date)
  const start = new Date()
  const days = getDaysDiff(start, end)
  const result = { days: 0, displayText: '' }

  if (days < 1) {
    result.days = 0
    result.displayText = '0 days'
  } else if (days === 1) {
    result.days = 1
    result.displayText = '1 day'
  } else {
    result.days = days
    result.displayText = `${days} days`
  }

  return result
}

export const daysSince = (date: Scalars['DateType']) => {
  const start = startOfDay(new Date(date))
  const end = new Date()
  const days = getDaysDiff(start, end)

  if (days < 1) {
    return 0
  } else {
    return days
  }
}

export const daysLeft = (
  date: Scalars['DateType'],
  daysTotal: number,
  start = new Date(),
) => {
  const end = add(date, { days: daysTotal + 1 })

  const days = getDaysDiff(start, end)

  const result = { days: 0, displayText: '' }

  if (days < 1) {
    result.days = 0
    result.displayText = '0 days'
  } else if (days === 1) {
    result.days = 1
    result.displayText = '1 day'
  } else {
    result.days = days
    result.displayText = `${days} days`
  }

  return result
}

export const daysDiff = (
  start: Scalars['DateType'],
  end: Scalars['DateType'],
): number => {
  start = startOfDay(new Date(start))
  end = new Date(end)
  const days = Math.floor((end - start) / 86400000)

  if (days < 1) {
    return 0
  } else {
    return days
  }
}

export const getCurrentUserTimezone = (abbreviated = false) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  if (abbreviated) {
    return getShortTimeZone()
  }
  return timezone
}

export const fullDateAndTime = (date: Scalars['DateType']) => {
  return `${format(date, getLongDateFormat())} ${format(
    date,
    HOURS_CAP_AM_PM_FORMAT,
  )}`
}

export const prettyMonthDayYear = (
  date: Scalars['DateType'],
  options?: {
    format?: string
  },
): string => {
  return format(date, options?.format || PRETTY_MONTH_DAY_YEAR_FORMAT)
}

export const getFirstMondayOfYear = (year: number) => {
  //new years day is a holiday so no work (skip it)
  const date = new Date(year, 0, 2)
  const dayOfWeek = date.getDay()
  return dayOfWeek !== 1 ? new Date(year, 0, 2 + (8 - dayOfWeek)) : date
}

export const shortDate = (date: Scalars['DateType']) => {
  const navigatorLanguageIsUS = navigator.language === 'en-US'
  const timeFormat = navigatorLanguageIsUS
    ? PRETTY_MONTH_NUM_DAY_FORMAT
    : DAY_MONTH_SHORT_FORMAT

  return format(date, timeFormat)
}

export const roundToNextHour = (date: Date) => {
  date.setHours(date.getHours() + 1)
  date.setMinutes(0)
  return date
}

export const isDateBeforeToday = (date: Scalars['DateType']): boolean => {
  const today = new Date()
  return isBeforeDate(date, today)
}

export const isThisYear = (date: Scalars['DateType']): boolean => {
  const originalDate = new Date(date)
  return isSame(originalDate, new Date(), 'year')
}

export const shortDateConditionalYear = (date: Scalars['DateType']) => {
  return isThisYear(date) ? shortDate(date) : prettyMonthDayYear(date)
}

export const fromTodayDiff = (date: Scalars['DateType']) => {
  const newDate = new Date(date)
  const diff = distanceFromNowInWords(newDate)

  switch (diff) {
    case 'an hour ago':
      return '1 hour ago'
    case 'a day ago':
      return '1 day ago'
    case 'a few seconds ago':
      return 'A few seconds ago'
    default:
      return diff
  }
}

export const getTimeInRange = (startDate: Date, endDate: Date) => {
  const unitsShown = Math.abs(getDaysDiff(endDate, startDate)) + 1
  const daysOfWeek = [...Array(unitsShown)].map((_, i) => {
    const start = new Date(startDate)

    return add(start, { days: i })
  })
  return daysOfWeek
}

export const getLocaleDateFormat = () => {
  const result = getLongDateFormat()
  return result
}

/** Array of date objects starting with Sunday and ending with Saturday */
export type WeekDates = Array<Date>

/**
 * @description given a date object, return an array of date objects that the date falls in
 * @example if passing in Wed November 18 2020, return array containing date objects from Sun November 15 2020 - Sat November 21 2020
 */
export const getContainingWeekFromDate = (
  date: Date = new Date(),
): WeekDates => {
  const weekDates: WeekDates = []

  if (date.toDateString() === 'Invalid Date') {
    date = new Date()
  }

  // set date to sunday
  date.setDate(date.getDate() - date.getDay())

  // create new date objects from sunday to saturday of the date passed in
  for (let i = 0; i < 7; i++) {
    const tempDate = new Date(date)

    tempDate.setDate(tempDate.getDate() + i)

    weekDates.push(tempDate)
  }

  return weekDates
}

export const calendarDaysDiff = (
  start: Scalars['DateType'],
  end: Scalars['DateType'],
): number => {
  return getCalendarDaysDiff(start, end)
}

const dateHelpers = {
  lastActivityStatus,
  prettyDateTime,
  defaultNavigatorTimeFormat,
  prettyDate,
  daysUntil,
  daysSince,
  daysDiff,
  daysLeft,
  fullDateAndTime,
  shortDate,
  roundToNextHour,
  getCurrentUserTimezone,
  prettyMonthDayYear,
  isDateBeforeToday,
  shortDateConditionalYear,
  fromTodayDiff,
  getTimeInRange,
  getLocaleDateFormat,
  prettyTimeEntryModalDateTime,
  getContainingWeekFromDate,
  calendarDaysDiff,
}

export default dateHelpers
